import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import authService, { type UserInfo } from '../services/authService'
import onboardingService from '../services/onboardingService'
import type { LoginRequest, RegisterRequest } from '../services/authService'
import type { UserRole } from '../types/user'
// Use the example avatar from public assets directory
// File location: apps/web/public/assets/example-avatar.png
// Vite serves public directory at root, so path is /assets/example-avatar.png
const exampleAvatarUrl = '/assets/example-avatar.png'

/**
 * Authentication Store (Pinia)
 * Manages global authentication state, tokens, and user info
 */

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<UserInfo | null>(null)
  const accessToken = ref<string | null>(null)
  const refreshToken = ref<string | null>(null)
  const isAuthenticated = ref<boolean>(false)
  const isLoading = ref<boolean>(false)
  const error = ref<string | null>(null)
  const currentRole = ref<string>('Admin') // Current viewing role (Admin/Editor) - admins can switch this
  const userRole = ref<UserRole | null>(null) // Current typed user role for route guards
  const actualUserRole = ref<UserRole | null>(null) // User's actual role from backend (cannot be changed)
  const permissions = ref<string[]>([]) // User permissions array
  const userName = ref<string>('User') // User display name
  // User avatar - stored value (for backward compatibility)
  const userAvatarStored = ref<string | undefined>(undefined)
  
  // User avatar computed - uses avatar_url from API if available, otherwise undefined for empty placeholder
  const userAvatar = computed<string | undefined>(() => {
    // Priority: avatar_url from user object (from API) > stored userAvatar (if not example avatar)
    if ((user.value as any)?.avatar_url) {
      return (user.value as any).avatar_url
    } else if (userAvatarStored.value && userAvatarStored.value !== exampleAvatarUrl) {
      return userAvatarStored.value
    }
    // Return undefined for empty placeholder - NvxAvatar will show initials
    return undefined
  })
  const hasCompletedOnboarding = ref<boolean>(false) // Whether user has completed onboarding
  const viewingProfileContext = ref<{ userId: string | null; name: string | null }>({ userId: null, name: null }) // NEW: Current profile being viewed/managed
  
  /**
   * Organization ID for the current user.
   * - null: Self-serve profile (signed up without invitation)
   * - string (UUID): Business-connected profile (invited by organization)
   * 
   * This determines navigation items shown to profile users:
   * - Self-serve profiles see Settings tab
   * - Business profiles do NOT see Settings tab
   */
  const organizationId = ref<string | null>(null)
  
  /**
   * Computed: Check if user is a self-serve profile (no organization)
   * Self-serve profiles are users who signed up without an invitation.
   * They have role="profile" and organizationId=null.
   * These users see a Settings tab in their navigation.
   */
  const isSelfServeProfile = computed(() => 
    userRole.value === 'profile' && !organizationId.value
  )

  // Initialize from localStorage
  const initializeAuth = () => {
    const storedAccessToken = localStorage.getItem('access_token')
    const storedRefreshToken = localStorage.getItem('refresh_token')
    const storedUser = localStorage.getItem('user')
    const storedRole = localStorage.getItem('current_role')
    const storedUserName = localStorage.getItem('user_name')
    const storedUserAvatar = localStorage.getItem('user_avatar')

    if (storedAccessToken) {
      accessToken.value = storedAccessToken
      isAuthenticated.value = true
    }

    if (storedRefreshToken) {
      refreshToken.value = storedRefreshToken
    }

    if (storedUser) {
      try {
        user.value = JSON.parse(storedUser)
        // If we have user data, try to get role from user object first (most reliable)
        const userRoleFromData = (user.value as any)?.role
        if (userRoleFromData) {
          const normalized = userRoleFromData.toLowerCase()
          const typedRole: UserRole = normalized === 'admin' ? 'admin' : normalized === 'profile' ? 'profile' : 'editor'
          actualUserRole.value = typedRole
          localStorage.setItem('actual_user_role', typedRole)
          console.log('[AuthStore.initializeAuth] Set actualUserRole from user data:', typedRole)
        }
      } catch (e) {
        console.error('Failed to parse stored user data:', e)
      }
    }

    // Restore actual user role (what they're authorized as) - prioritize stored actual role
    const storedActualRole = localStorage.getItem('actual_user_role')
    if (storedActualRole) {
      const normalized = storedActualRole.toLowerCase()
      actualUserRole.value = normalized === 'admin' ? 'admin' : normalized === 'profile' ? 'profile' : 'editor'
      // Also persist to localStorage if not already there in correct format
      localStorage.setItem('actual_user_role', actualUserRole.value)
    }

    // Set current viewing role (userRole) - this can be different from actualUserRole if user switched roles
    if (storedRole) {
      currentRole.value = storedRole
      // Set typed userRole based on stored role
      const normalized = storedRole.toLowerCase()
      userRole.value = normalized === 'admin' ? 'admin' : normalized === 'profile' ? 'profile' : 'editor'
    } else if (actualUserRole.value) {
      // If no stored role but we have actual role, use actual role as default
      userRole.value = actualUserRole.value
      currentRole.value = actualUserRole.value === 'admin' ? 'Admin' : actualUserRole.value === 'profile' ? 'Profile' : 'Editor'
      localStorage.setItem('current_role', currentRole.value)
    }

    // Restore permissions from localStorage
    const storedPermissions = localStorage.getItem('permissions')
    if (storedPermissions) {
      try {
        permissions.value = JSON.parse(storedPermissions)
      } catch (e) {
        console.error('Failed to parse stored permissions:', e)
        permissions.value = []
      }
    }

    if (storedUserName) {
      userName.value = storedUserName
    }

    if (storedUserAvatar) {
      try {
        // Only use stored avatar if it's not null/empty and not the example avatar
        userAvatarStored.value = (storedUserAvatar === 'null' || storedUserAvatar === '' || storedUserAvatar === exampleAvatarUrl) 
          ? undefined 
          : storedUserAvatar
      } catch (e) {
        // Ignore parsing errors, use undefined (empty placeholder)
        userAvatarStored.value = undefined
      }
    } else {
      // If no stored avatar, use undefined (empty placeholder)
      userAvatarStored.value = undefined
    }

    // Restore viewing profile context
    const storedViewingContext = localStorage.getItem('viewing_profile_context')
    if (storedViewingContext) {
      try {
        viewingProfileContext.value = JSON.parse(storedViewingContext)
      } catch (e) {
        console.error('Failed to parse stored viewing context:', e)
        viewingProfileContext.value = { userId: null, name: null }
      }
    }

    // Restore onboarding status
    const storedOnboardingStatus = localStorage.getItem('has_completed_onboarding')
    if (storedOnboardingStatus === 'true') {
      hasCompletedOnboarding.value = true
    }
    
    // Restore organization ID from localStorage
    const storedOrgId = localStorage.getItem('organization_id')
    if (storedOrgId !== null) {
      organizationId.value = storedOrgId === '' ? null : storedOrgId
    }
  }

  // Computed
  const isLoggedIn = computed(() => isAuthenticated.value && !!accessToken.value)

  // User ID from stored user info
  const userId = computed(() => user.value?.id || null)

  // User email from stored user info
  const userEmail = computed(() => user.value?.email || null)

  // User profile for UI components
  const userProfile = computed(() => {
    // Use LinkedIn profile image if available, otherwise show empty placeholder (no image)
    let avatar: string | undefined = undefined
    
    // Use the computed userAvatar which already handles LinkedIn profile image
      avatar = userAvatar.value
    // If no avatar, avatar remains undefined - NvxAvatar will show initials/empty placeholder
    
    return {
    name: userName.value,
    role: currentRole.value,
      avatar: avatar
    }
  })

  // Check if user has a specific permission
  const checkPermission = (permission: string): boolean => {
    // Admins have all permissions
    if (userRole.value === 'admin') return true
    return permissions.value.includes(permission)
  }

  // Check if user is admin (based on actual role from backend, not viewing role)
  const isAdmin = computed(() => actualUserRole.value === 'admin')

  // Check if user is editor (based on actual role from backend, not viewing role)
  const isEditor = computed(() => actualUserRole.value === 'editor')

  // Actions
  /**
   * Login user
   */
  async function login(credentials: LoginRequest) {
    try {
      isLoading.value = true
      error.value = null

      const response = await authService.login(credentials)

      // Store tokens
      accessToken.value = response.access_token
      if (response.refresh_token) {
        refreshToken.value = response.refresh_token
        localStorage.setItem('refresh_token', response.refresh_token)
      }

      localStorage.setItem('access_token', response.access_token)

      // Store initial user info from login response
      user.value = response.user
      localStorage.setItem('user', JSON.stringify(response.user))

      // After login, fetch full user profile from /api/b2b/users/me to get the correct role
      try {
        const userProfile = await authService.getCurrentUser()
        console.log('[AuthStore.login] User profile from /api/b2b/users/me:', userProfile)

        user.value = userProfile
        localStorage.setItem('user', JSON.stringify(userProfile))

        // Extract role from the full user profile
        const role = userProfile.role || (response.user as any).role || 'editor'
        const normalizedRole = role.toLowerCase()
        const typedRole: UserRole = normalizedRole === 'admin' ? 'admin' : normalizedRole === 'profile' ? 'profile' : 'editor'

        console.log('[AuthStore.login] User role from profile:', role, '-> typed:', typedRole)

        // Set the actual user role (what they're authorized as from backend)
        actualUserRole.value = typedRole
        localStorage.setItem('actual_user_role', typedRole)

        // Set the current viewing role (can be changed by admins/editors)
        userRole.value = typedRole
        currentRole.value = normalizedRole === 'admin' ? 'Admin' : normalizedRole === 'profile' ? 'Profile' : 'Editor'
        localStorage.setItem('current_role', currentRole.value)

        // Update user name from profile
        const profileName = userProfile.name || userProfile.first_name || response.user.name
        if (profileName) {
          userName.value = profileName
          localStorage.setItem('user_name', profileName)
        }
        
        // Set organization ID from user profile (for self-serve vs business profile distinction)
        organizationId.value = userProfile.organization_id || null
        localStorage.setItem('organization_id', organizationId.value || '')
        console.log('[AuthStore.login] Organization ID:', organizationId.value)
      } catch (profileError) {
        console.warn('[AuthStore.login] Failed to fetch user profile, using login response role:', profileError)
        // Fallback to login response role
        const role = (response.user as any).role || 'editor'
        const normalizedRole = role.toLowerCase()
        const typedRole: UserRole = normalizedRole === 'admin' ? 'admin' : normalizedRole === 'profile' ? 'profile' : 'editor'

        actualUserRole.value = typedRole
        localStorage.setItem('actual_user_role', typedRole)
        userRole.value = typedRole
        currentRole.value = normalizedRole === 'admin' ? 'Admin' : normalizedRole === 'profile' ? 'Profile' : 'Editor'
        localStorage.setItem('current_role', currentRole.value)
      }

      // Extract and store permissions if provided
      const userPermissions = (response.user as any).permissions || []
      permissions.value = userPermissions
      localStorage.setItem('permissions', JSON.stringify(userPermissions))

      // Set user name
      if (response.user.name) {
        userName.value = response.user.name
        localStorage.setItem('user_name', response.user.name)
      }

      isAuthenticated.value = true

      // Check onboarding status after login
      try {
        const onboardingStatus = await onboardingService.getOnboardingStatus()
        hasCompletedOnboarding.value = onboardingStatus.completed
        localStorage.setItem('has_completed_onboarding', String(onboardingStatus.completed))
        console.log('[AuthStore.login] Onboarding status:', onboardingStatus.completed)
      } catch (onboardingError) {
        console.warn('[AuthStore.login] Failed to check onboarding status:', onboardingError)
        // Default to not completed if we can't check
        hasCompletedOnboarding.value = false
      }

      return { success: true, data: response }
    } catch (err: any) {
      error.value = err.message || 'Login failed'
      isAuthenticated.value = false
      return { success: false, error: err.message }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Register new user
   * 
   * NOTE: This function does NOT auto-login the user after registration.
   * Tokens returned from the backend are intentionally ignored.
   * User must sign in separately after successful registration.
   * 
   * @returns {success: boolean, message?: string, error?: string}
   */
  async function register(userData: RegisterRequest) {
    try {
      isLoading.value = true
      error.value = null

      // Call register API - tokens will be returned but we intentionally ignore them
      await authService.register(userData)

      // SUCCESS - but do NOT store tokens or set authenticated state
      // User must sign in separately after registration
      console.log('[AuthStore.register] Registration successful - user must sign in separately')
      
      return { success: true, message: 'Account created successfully' }
    } catch (err: any) {
      error.value = err.message || 'Registration failed'
      console.error('[AuthStore.register] Registration failed:', err)
      return { success: false, error: err.message }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Clear all localStorage items including pattern-based cache keys
   */
  function clearAllLocalStorage() {
    // Clear all known explicit keys
    const knownKeys = [
      'access_token',
      'refresh_token',
      'user',
      'organization_id',
      'current_role',
      'user_name',
      'user_avatar',
      'actual_user_role',
      'permissions',
      'viewing_profile_context',
      'has_completed_onboarding',
      'organization_logo_url',
      'onboardingData',
      'onboardingCurrentStep',
      'onboardingCurrentStage',
      'onboardingInProgress',
      'linkedInUser',
      'last_activity_ts'
    ]
    
    knownKeys.forEach(key => {
      localStorage.removeItem(key)
    })
    
    // Clear analytics cache (pattern-based keys: nvx_analytics_* and nvx_posts_*)
    const analyticsCachePrefix = 'nvx_analytics_'
    const postsCachePrefix = 'nvx_posts_'
    const keysToRemove: string[] = []
    
    // Iterate through all localStorage keys
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && (key.startsWith(analyticsCachePrefix) || key.startsWith(postsCachePrefix))) {
        keysToRemove.push(key)
      }
    }
    
    keysToRemove.forEach(key => {
      localStorage.removeItem(key)
    })
    
    console.log('[AuthStore.clearAllLocalStorage] Cleared', knownKeys.length + keysToRemove.length, 'localStorage items')
  }

  /**
   * Clear all sessionStorage items
   */
  function clearAllSessionStorage() {
    try {
      sessionStorage.clear()
      console.log('[AuthStore.clearAllSessionStorage] Cleared all sessionStorage')
    } catch (err) {
      console.warn('[AuthStore.clearAllSessionStorage] Error clearing sessionStorage:', err)
    }
  }

  /**
   * Reset all Pinia stores to initial state
   */
  async function resetAllStores() {
    try {
      // Import stores dynamically to avoid circular dependencies
      const { useOnboardingStore } = await import('./onboarding')
      const { useAnalyticsStore } = await import('./analytics')
      const { usePostCreationStore } = await import('./postCreation')
      const { useProfilesStore } = await import('./profiles')
      
      // Reset onboarding store
      try {
        const onboardingStore = useOnboardingStore()
        if (onboardingStore.reset) {
          onboardingStore.reset()
        }
      } catch (err) {
        console.warn('[AuthStore.resetAllStores] Error resetting onboarding store:', err)
      }
      
      // Reset analytics store
      try {
        const analyticsStore = useAnalyticsStore()
        if (analyticsStore.reset) {
          analyticsStore.reset()
        }
      } catch (err) {
        console.warn('[AuthStore.resetAllStores] Error resetting analytics store:', err)
      }
      
      // Reset post creation store
      try {
        const postCreationStore = usePostCreationStore()
        if (postCreationStore.reset) {
          postCreationStore.reset()
        }
      } catch (err) {
        console.warn('[AuthStore.resetAllStores] Error resetting post creation store:', err)
      }
      
      // Reset profiles store (uses Pinia's built-in $reset)
      try {
        const profilesStore = useProfilesStore()
        if (profilesStore.$reset) {
          profilesStore.$reset()
        }
      } catch (err) {
        console.warn('[AuthStore.resetAllStores] Error resetting profiles store:', err)
      }
      
      console.log('[AuthStore.resetAllStores] Reset all stores')
    } catch (err) {
      console.warn('[AuthStore.resetAllStores] Error resetting stores:', err)
    }
  }

  /**
   * Logout user - clears all cached data, session data, and localStorage
   */
  async function logout() {
    try {
      isLoading.value = true
      await authService.logout()
    } catch (err) {
      console.error('[AuthStore.logout] Logout API error:', err)
      // Continue with cleanup even if API call fails
    } finally {
      // Clear all state regardless of API call success
      user.value = null
      accessToken.value = null
      refreshToken.value = null
      isAuthenticated.value = false
      error.value = null
      organizationId.value = null
      userRole.value = null
      actualUserRole.value = null
      permissions.value = []
      userAvatarStored.value = undefined
      hasCompletedOnboarding.value = false
      viewingProfileContext.value = { userId: null, name: null }

      // Clear all localStorage (including pattern-based cache keys)
      clearAllLocalStorage()

      // Clear all sessionStorage
      clearAllSessionStorage()

      // Clear LinkedIn service cached data
      try {
        const { default: linkedInService } = await import('../services/linkedinService')
        linkedInService.clearStoredUserData()
      } catch (err) {
        console.warn('[AuthStore.logout] Error clearing LinkedIn service data:', err)
      }

      // Reset all other Pinia stores
      await resetAllStores()

      isLoading.value = false
      console.log('[AuthStore.logout] Logout complete - all cached data, session data, and localStorage cleared')
    }
  }

  /**
   * Forgot password
   */
  async function forgotPassword(email: string) {
    try {
      isLoading.value = true
      error.value = null

      const response = await authService.forgotPassword(email)
      return { success: true, data: response }
    } catch (err: any) {
      error.value = err.message || 'Failed to send password reset email'
      return { success: false, error: err.message }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Reset password
   */
  async function resetPassword(newPassword: string, passwordVerification: string, token?: string) {
    try {
      isLoading.value = true
      error.value = null

      const response = await authService.resetPassword(newPassword, passwordVerification, token)
      return { success: true, data: response }
    } catch (err: any) {
      error.value = err.message || 'Failed to reset password'
      return { success: false, error: err.message }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Handle Google login - redirects to Google OAuth
   */
  async function handleGoogleLogin() {
    try {
      isLoading.value = true
      error.value = null
      
      const url = await authService.initiateGoogleOAuth()
      // Redirect to Google OAuth
      window.location.href = url
      return { success: true }
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to initiate Google login'
      error.value = errorMessage
      console.error('[AuthStore.handleGoogleLogin] Error:', err)
      return { success: false, error: errorMessage }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Get current user info
   * @param options.skipLogoutOn401 - If true, do not call logout() on 401 (e.g. in OAuth callback where B2B may not exist)
   */
  async function fetchCurrentUser(options?: { skipLogoutOn401?: boolean }) {
    try {
      isLoading.value = true
      error.value = null

      const userInfo = await authService.getCurrentUser()
      user.value = userInfo
      localStorage.setItem('user', JSON.stringify(userInfo))

      // Set user name from user info (for userProfile computed)
      if (userInfo.name) {
        userName.value = userInfo.name
        localStorage.setItem('user_name', userInfo.name)
      } else if (userInfo.first_name || userInfo.last_name) {
        const fullName = `${userInfo.first_name || ''} ${userInfo.last_name || ''}`.trim()
        if (fullName) {
          userName.value = fullName
          localStorage.setItem('user_name', fullName)
        }
      }

      // Set user avatar from avatar_url if available
      if ((userInfo as any).avatar_url) {
        userAvatarStored.value = (userInfo as any).avatar_url
        localStorage.setItem('user_avatar', (userInfo as any).avatar_url)
      }
      
      // Set organization ID from user info
      organizationId.value = userInfo.organization_id || null
      localStorage.setItem('organization_id', organizationId.value || '')

      // Update role from fetched user info to ensure it's always correct
      const role = userInfo.role || 'editor'
      const normalizedRole = role.toLowerCase()
      const typedRole: UserRole = normalizedRole === 'admin' ? 'admin' : normalizedRole === 'profile' ? 'profile' : 'editor'

      // Update actual user role (what they're authorized as from backend)
      actualUserRole.value = typedRole
      localStorage.setItem('actual_user_role', typedRole)

      // Only update userRole if it's not already set or if it doesn't match actual role
      // This preserves the user's role switching preference
      if (!userRole.value || userRole.value !== typedRole) {
        userRole.value = typedRole
        currentRole.value = normalizedRole === 'admin' ? 'Admin' : normalizedRole === 'profile' ? 'Profile' : 'Editor'
        localStorage.setItem('current_role', currentRole.value)
      }
      
      // Mark as authenticated
      isAuthenticated.value = true

      return { success: true, data: userInfo }
    } catch (err: any) {
      error.value = err.message || 'Failed to fetch user info'
      // If unauthorized, clear auth state unless caller asked to skip (e.g. self-serve OAuth callback)
      if (err.response?.status === 401 && !options?.skipLogoutOn401) {
        await logout()
      }
      return { success: false, error: err.message }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Clear error
   */
  function clearError() {
    error.value = null
  }

  /**
   * Set tokens manually (useful for token refresh)
   */
  function setTokens(access: string, refresh?: string) {
    accessToken.value = access
    localStorage.setItem('access_token', access)
    isAuthenticated.value = true // Mark as authenticated when tokens are set

    if (refresh) {
      refreshToken.value = refresh
      localStorage.setItem('refresh_token', refresh)
    }
  }

  /**
   * Set user data directly from OAuth response
   * This avoids calling fetchCurrentUser() which may fail for new users
   * @param userData - User data from OAuth callback response
   * @param tokens - Access and refresh tokens
   */
  function setUserFromOAuth(userData: {
    id: string
    email: string
    name?: string
    role?: string
    organization_id?: string
    first_name?: string
    last_name?: string
    avatar_url?: string
  }, tokens: { access_token: string; refresh_token?: string }) {
    // Store tokens first
    accessToken.value = tokens.access_token
    localStorage.setItem('access_token', tokens.access_token)
    
    if (tokens.refresh_token) {
      refreshToken.value = tokens.refresh_token
      localStorage.setItem('refresh_token', tokens.refresh_token)
    }

    // Store user data
    user.value = userData as UserInfo
    localStorage.setItem('user', JSON.stringify(userData))

    // Set user name
    const displayName = userData.name || userData.first_name || userData.email.split('@')[0]
    userName.value = displayName
    localStorage.setItem('user_name', displayName)

    // Set avatar if available
    if (userData.avatar_url) {
      userAvatarStored.value = userData.avatar_url
      localStorage.setItem('user_avatar', userData.avatar_url)
    }

    // Set role - default to 'profile' for OAuth users (self-serve)
    const role = userData.role || 'profile'
    const normalizedRole = role.toLowerCase()
    const typedRole: UserRole = normalizedRole === 'admin' ? 'admin' : normalizedRole === 'profile' ? 'profile' : 'editor'

    actualUserRole.value = typedRole
    localStorage.setItem('actual_user_role', typedRole)
    userRole.value = typedRole
    currentRole.value = normalizedRole === 'admin' ? 'Admin' : normalizedRole === 'profile' ? 'Profile' : 'Editor'
    localStorage.setItem('current_role', currentRole.value)

    // Set organization ID (null for self-serve OAuth users)
    organizationId.value = userData.organization_id || null
    localStorage.setItem('organization_id', organizationId.value || '')

    // Mark as authenticated
    isAuthenticated.value = true

    console.log('[AuthStore.setUserFromOAuth] User set from OAuth:', {
      id: userData.id,
      email: userData.email,
      role: typedRole,
      organizationId: organizationId.value
    })
  }

  /**
   * Set current role (viewing role)
   * - Admin users can switch between Admin and Profile views (NOT Editor)
   * - Editor users can switch between Editor and Profile views
   * - Profile users cannot change their role
   */
  function setCurrentRole(role: string) {
    console.log('[AuthStore.setCurrentRole] Called with role:', role)
    console.log('[AuthStore.setCurrentRole] Current state - actualUserRole:', actualUserRole.value, 'userRole:', userRole.value, 'isAuthenticated:', isAuthenticated.value, 'accessToken:', !!accessToken.value)

    const normalizedRole = role.toLowerCase()
    
    // Validate role switching based on actual user role
    if (actualUserRole.value === 'admin') {
      // Admin can only switch to Admin or Profile (NOT Editor)
      if (normalizedRole === 'editor') {
        console.warn('[AuthStore.setCurrentRole] Admin cannot switch to editor role')
        return
      }
      if (normalizedRole !== 'admin' && normalizedRole !== 'profile') {
        console.warn('[AuthStore.setCurrentRole] Invalid role for admin:', normalizedRole)
        return
      }
    } else if (actualUserRole.value === 'editor') {
      // Editor can only switch to Editor or Profile
      if (normalizedRole === 'admin') {
        console.warn('[AuthStore.setCurrentRole] Editor cannot switch to admin role')
        return
      }
      if (normalizedRole !== 'editor' && normalizedRole !== 'profile') {
        console.warn('[AuthStore.setCurrentRole] Invalid role for editor:', normalizedRole)
        return
      }
    } else {
      // Profile users cannot switch roles
      console.warn('[AuthStore.setCurrentRole] Profile users cannot switch roles')
      return
    }

    currentRole.value = normalizedRole === 'admin' ? 'Admin' : normalizedRole === 'profile' ? 'Profile' : 'Editor'
    userRole.value = normalizedRole === 'admin' ? 'admin' : normalizedRole === 'profile' ? 'profile' : 'editor'
    localStorage.setItem('current_role', currentRole.value)

    console.log('[AuthStore.setCurrentRole] After change - currentRole:', currentRole.value, 'userRole:', userRole.value)
    console.log('[AuthStore.setCurrentRole] isLoggedIn should be:', isAuthenticated.value && !!accessToken.value)
  }

  /**
   * Check if user can switch roles
   * - Admins and editors can switch to Profile view
   * - Profile users cannot switch (only one role available)
   */
  const canSwitchRoles = computed(() => {
    return actualUserRole.value === 'admin' || actualUserRole.value === 'editor'
  })

  /**
   * Get available roles based on actual user role
   * - Admin: can switch between Admin and Profile
   * - Editor: can switch between Editor and Profile
   * - Profile: only Profile (no switching)
   */
  const availableRoles = computed(() => {
    if (actualUserRole.value === 'admin') {
      return [
        { value: 'Admin', label: 'Admin' },
        { value: 'Profile', label: 'Profile' }
      ]
    } else if (actualUserRole.value === 'editor') {
      return [
        { value: 'Editor', label: 'Editor' },
        { value: 'Profile', label: 'Profile' }
      ]
    } else {
      // Profile users only see Profile
      return [
        { value: 'Profile', label: 'Profile' }
      ]
    }
  })

  /**
   * Set user profile
   */
  function setUserProfile(profile: { name?: string; role?: string; avatar?: string | undefined }) {
    if (profile.name !== undefined) {
      userName.value = profile.name
      localStorage.setItem('user_name', profile.name)
    }
    if (profile.role !== undefined) {
      currentRole.value = profile.role
      localStorage.setItem('current_role', profile.role)
    }
    if (profile.avatar !== undefined) {
      userAvatarStored.value = profile.avatar
      localStorage.setItem('user_avatar', profile.avatar || 'null')
    }
  }

  /**
   * Sync display name and/or avatar from LinkedIn into the auth store.
   * Used for self-serve (profile, no org) users when we skip B2B users/me so the dashboard
   * and settings still show the correct name and avatar.
   */
  function syncProfileFromLinkedIn(name?: string, avatarUrl?: string) {
    if (name?.trim()) {
      userName.value = name.trim()
      localStorage.setItem('user_name', name.trim())
      if (user.value) {
        user.value = { ...user.value, name: name.trim() }
        localStorage.setItem('user', JSON.stringify(user.value))
      }
    }
    if (avatarUrl?.trim()) {
      userAvatarStored.value = avatarUrl.trim()
      localStorage.setItem('user_avatar', avatarUrl.trim())
      if (user.value) {
        user.value = { ...user.value, avatar_url: avatarUrl.trim() }
        localStorage.setItem('user', JSON.stringify(user.value))
      }
    }
  }

  /**
   * Set onboarding completed status
   */
  function setOnboardingCompleted(completed: boolean) {
    hasCompletedOnboarding.value = completed
    localStorage.setItem('has_completed_onboarding', String(completed))
  }

  /**
   * Set viewing profile context (for multi-profile post creation)
   * @param userId - Profile user ID to view/manage, null for self
   * @param name - Profile user name
   */
  function setViewingProfileContext(userId: string | null, name: string | null = null) {
    viewingProfileContext.value = { userId, name }
    if (userId) {
      localStorage.setItem('viewing_profile_context', JSON.stringify({ userId, name }))
    } else {
      localStorage.removeItem('viewing_profile_context')
    }
  }

  /**
   * Clear viewing profile context (return to self)
   */
  function clearViewingProfileContext() {
    viewingProfileContext.value = { userId: null, name: null }
    localStorage.removeItem('viewing_profile_context')
  }

  // Initialize on store creation
  initializeAuth()

  return {
    // State
    user,
    accessToken,
    refreshToken,
    isAuthenticated,
    isLoading,
    error,
    currentRole,
    userRole,
    actualUserRole,
    permissions,
    userName,
    userAvatar,
    hasCompletedOnboarding,
    viewingProfileContext,
    organizationId,

    // Computed
    isLoggedIn,
    userId,
    userEmail,
    userProfile,
    isAdmin,
    isEditor,
    canSwitchRoles,
    availableRoles,
    isSelfServeProfile,

    // Functions
    checkPermission,

    // Actions
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    handleGoogleLogin,
    fetchCurrentUser,
    clearError,
    setTokens,
    setUserFromOAuth,
    setCurrentRole,
    setUserProfile,
    syncProfileFromLinkedIn,
    setOnboardingCompleted,
    setViewingProfileContext,
    clearViewingProfileContext,
    initializeAuth
  }
})
